'use client'

import React, { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { useParams, useRouter } from 'next/navigation'
import { Mail, ExternalLink, Trophy, Search, LayoutGrid } from 'lucide-react'
import WorkView from './WorkView'
import GeometricBackground from './GeometricBackground'
import './portfolio.css'

const Linkedin = ({ size = 18, ...props }: React.SVGProps<SVGSVGElement> & { size?: number }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect width="4" height="12" x="2" y="9" />
    <circle cx="4" cy="4" r="2" />
  </svg>
)

const Medium = ({ size = 18, ...props }: React.SVGProps<SVGSVGElement> & { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M13.54 12a6.8 6.8 0 01-6.77 6.82A6.8 6.8 0 010 12a6.8 6.8 0 016.77-6.82A6.8 6.8 0 0113.54 12zm7.42 0c0 3.54-1.51 6.42-3.38 6.42-1.87 0-3.39-2.88-3.39-6.42s1.52-6.42 3.39-6.42c1.87 0 3.38 2.88 3.38 6.42zm2.81 0c0 3.14-.54 5.68-1.21 5.68-.66 0-1.2-.254-1.2-5.68s.54-5.68 1.2-5.68c.67 0 1.21 2.54 1.21 5.68z" />
  </svg>
)

const NAV_ITEMS = [
  { id: 'about', label: 'About', href: '/cherylchen', scrollToTop: true },
  { id: 'work', label: 'Work', href: '/cherylchen#work' },
  {
    id: 'resume',
    label: 'Resume',
    href: 'https://cdn.jsdelivr.net/gh/cxytw/resume@publish/Shin-Ying_CV.pdf',
    external: true,
    isButton: true,
  },
] as const

export default function Portfolio() {
  const params = useParams()
  const router = useRouter()
  // Extract slug from URL if we're on a project detail route
  const initialSlug = params?.slug as string | undefined

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isMenuClosing, setIsMenuClosing] = useState(false)
  const isProjectOpen = !!initialSlug
  const [scrolled, setScrolled] = useState(false)
  const [activeSection, setActiveSection] = useState<string>('about')
  const [deselectFlag, setDeselectFlag] = useState(0)
  const [pendingScroll, setPendingScroll] = useState<string | null>(null)

  // Handle scroll-after-navigation: when navigating from a project detail page
  // back to the main page, scroll to the appropriate section after mount
  useEffect(() => {
    if (!isProjectOpen && pendingScroll) {
      // Small delay to ensure DOM is ready
      requestAnimationFrame(() => {
        if (pendingScroll === 'top') {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        } else if (pendingScroll === 'work') {
          const el = document.getElementById('work')
          if (el) el.scrollIntoView({ behavior: 'smooth' })
        }
        setPendingScroll(null)
      })
    }
  }, [isProjectOpen, pendingScroll])

  const marqueeRef = useRef<HTMLDivElement>(null)
  const [isMobile, setIsMobile] = useState(false)

  // Detect mobile viewport for marquee optimization
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)')
    const update = () => setIsMobile(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])
  // Header stays transparent until content scrolls past the hero/cover area
  // On both mobile & desktop: hero is sticky, content scrolls over it,
  // header turns white when #work section reaches the top
  useEffect(() => {
    let ticking = false

    const update = () => {
      ticking = false

      if (isProjectOpen) {
        // On project detail pages: header turns white as soon as the user starts scrolling
        setScrolled(window.scrollY > 20)
        setActiveSection('')
      } else {
        // The hero section is position: sticky, so it's always at the top.
        // Instead, detect active section based on the #work element position.
        const workSection = document.getElementById('work')
        if (workSection) {
          const rect = workSection.getBoundingClientRect()
          // When the top of #work is at or above the header area (~80px), it's active
          // Otherwise, the hero/about area is in view
          setActiveSection(rect.top <= 100 ? 'work' : 'about')

          // Header transparency: turns white when #work reaches top
          setScrolled(rect.top <= 80)
        } else {
          setScrolled(window.scrollY > 20)
        }
      }
    }

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(update)
        ticking = true
      }
    }

    const handleResize = () => {
      if (!ticking) {
        requestAnimationFrame(update)
        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleResize, { passive: true })
    update() // Check initial state
    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
    }
  }, [isProjectOpen])

  // Prevent background scrolling when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMenuOpen])

  // Smoothly animate the drawer closed before removing it from the DOM
  const closeMenu = () => {
    if (!isMenuOpen) return
    setIsMenuClosing(true)
    window.setTimeout(() => {
      setIsMenuOpen(false)
      setIsMenuClosing(false)
    }, 360)
  }

  // ── Seamless infinite marquee ──────────────────────────────────────────
  useEffect(() => {
    if (isMobile) {
      // On mobile we render a single set (no animation). Make sure no leftover
      // inline transform from a previous desktop view is applied.
      const track = marqueeRef.current
      if (track) track.style.transform = ''
      return
    }
    const track = marqueeRef.current
    if (!track) return

    let animationId: number
    let pos = 0
    const speed = 0.6 // pixels per frame at 60fps ≈ 36px/s

    const step = () => {
      pos += speed
      // The track contains 6 identical sets.
      // We only need to scroll by the width of EXACTLY ONE set to make it seamless.
      // Total scrollWidth is 6 sets. One set is scrollWidth / 6.
      // However, to be perfectly accurate with gaps, we add 1 gap to scrollWidth if the gap isn't at the end.
      // Actually, flex gap is only between items.
      // Total items = 24. Total gaps = 23.
      // Width of 1 set of 4 items = 4 * itemWidth + 4 * gap.
      // We can calculate the exact width of one set by taking the offset of the 5th item.
      const fifthItem = track.children[4] as HTMLElement
      if (fifthItem) {
        const setWidth = fifthItem.offsetLeft - (track.children[0] as HTMLElement).offsetLeft
        if (pos >= setWidth) {
          pos -= setWidth // Seamless reset
        }
      }

      track.style.transform = `translateX(-${pos}px)`
      animationId = requestAnimationFrame(step)
    }

    animationId = requestAnimationFrame(step)

    // Pause on hover
    const onMouseEnter = () => cancelAnimationFrame(animationId)
    const onMouseLeave = () => {
      animationId = requestAnimationFrame(step)
    }
    track.addEventListener('mouseenter', onMouseEnter)
    track.addEventListener('mouseleave', onMouseLeave)

    return () => {
      cancelAnimationFrame(animationId)
      track.removeEventListener('mouseenter', onMouseEnter)
      track.removeEventListener('mouseleave', onMouseLeave)
    }
  }, [isMobile])

  return (
    <div className={`portfolio-root ${!isProjectOpen ? 'is-home-page' : ''}`}>
      {/* Portfolio Header */}
      <header
        className={`port-header${scrolled ? ' scrolled' : ''}${isProjectOpen ? ' is-project-open' : ''}${isMenuOpen ? ' menu-open' : ''}`}
      >
        <div className="port-header-container">
          <Link
            href="https://cherylchen.xyz/"
            className="port-logo"
            title="UI/UX Designer"
            onClick={() => {
              closeMenu()
              setDeselectFlag((f) => f + 1)
            }}
          >
            <span className="port-logo-title">
              Cheryl Chen<span className="port-logo-dot">.</span>
            </span>
          </Link>

          <div
            className={`port-nav ${isMenuOpen ? 'menu-open' : ''}${isMenuClosing ? ' closing' : ''}`}
          >
            {NAV_ITEMS.map((item, i) => {
              const isExternal = (item as any).external
              const isButton = (item as any).isButton
              const scrollToTop = (item as any).scrollToTop
              const isActive = !isProjectOpen && !isExternal && activeSection === item.id
              return (
                <Link
                  key={item.id}
                  href={item.href}
                  target={isExternal ? '_blank' : undefined}
                  rel={isExternal ? 'noopener noreferrer' : undefined}
                  className={
                    isButton ? 'port-nav-button' : `port-nav-link${isActive ? ' active' : ''}`
                  }
                  onClick={(e) => {
                    closeMenu()
                    if (isProjectOpen) {
                      // On project detail pages: navigate to main page first,
                      // then scroll after the page loads
                      e.preventDefault()
                      setPendingScroll(scrollToTop ? 'top' : 'work')
                      router.push('/cherylchen')
                      return
                    }
                    if (scrollToTop) {
                      // About: scroll to very top of page
                      e.preventDefault()
                      window.scrollTo({ top: 0, behavior: 'smooth' })
                    } else if (item.id === 'work') {
                      // Work: scroll to #work section
                      e.preventDefault()
                      const el = document.getElementById('work')
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth' })
                      }
                    }
                  }}
                >
                  {!isButton && (
                    <span className="port-nav-index">{String(i + 1).padStart(2, '0')}</span>
                  )}
                  {isButton ? (
                    <>
                      {item.label}
                      {isExternal && (
                        <ExternalLink size={16} strokeWidth={2.5} style={{ marginLeft: '2px' }} />
                      )}
                    </>
                  ) : (
                    <span className="port-nav-label">{item.label}</span>
                  )}
                </Link>
              )
            })}

            <div className="port-nav-menu-footer">
              <p className="port-nav-menu-tagline">UI/UX Designer · Taipei, Taiwan</p>
              <div className="port-nav-menu-social">
                <a href="mailto:cxytw77@gmail.com" title="Email" aria-label="Email">
                  <Mail size={18} />
                </a>
                <a
                  href="https://www.linkedin.com/in/shinyingchen/"
                  title="LinkedIn"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="LinkedIn"
                >
                  <Linkedin size={18} />
                </a>
                <a
                  href="https://medium.com/@xying77"
                  title="Medium"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Medium"
                >
                  <Medium size={18} />
                </a>
              </div>
            </div>
          </div>

          {/* Mobile Menu Trigger — animated hamburger to X */}
          <button
            className={`port-menu-trigger${isMenuOpen ? ' is-open' : ''}`}
            onClick={() => {
              if (isMenuOpen) {
                closeMenu()
              } else {
                setIsMenuOpen(true)
              }
            }}
            aria-label="Toggle Menu"
            aria-expanded={isMenuOpen}
          >
            <span className="port-menu-bar" />
            <span className="port-menu-bar" />
            <span className="port-menu-bar" />
          </button>
        </div>
      </header>

      {/* Main Content Area — All sections rendered on one page */}
      <main className={`port-main ${isProjectOpen ? 'is-project-open' : ''}`}>
        {/* Hero Section — sticky, content below slides over it */}
        {!isProjectOpen && (
          <section id="hero" className="port-section-wrapper hero-section hero-sticky">
            {/* Luminous bottom-center spotlight radiating from under View Selected Works */}
            <div className="hero-bottom-spotlight" />
            <div className="hero-ambient-glow glow-1" />
            <div className="hero-ambient-glow glow-3" />

            {/* Radiant glow behind the hero text — soft violet/indigo luminescence */}
            <div className="hero-text-glow" />

            {/* Animated geometric background — clean, spacious floating shapes */}
            <div className="geometric-bg-wrapper">
              <GeometricBackground
                configOverrides={{
                  shapeCount: 6,
                  maxOpacity: 0.38,
                  outlineChance: 0.3,
                  speed: 0.5,
                }}
                mobileConfig={{
                  shapeCount: 2,
                  maxOpacity: 0.25,
                  outlineChance: 0.15,
                  mobileSizeScale: 0.5,
                }}
              />
            </div>
            {/* Grainy / frosted glass texture overlay */}
            <div className="hero-noise-overlay"></div>
            <div className="hero-inner">
              <div className="hero-container flex-layout">
                <div className="hero-avatar-wrap">
                  <div className="hero-avatar-glow-ring" />
                  <img
                    src="/images/Cheryl%20Chen%20Photo%202.jpg"
                    alt="Cheryl Chen"
                    className="hero-avatar"
                  />
                </div>
                <div className="hero-content">
                  {/* Block 1: Role Tag */}
                  <div className="hero-block-tag">
                    <div className="hero-role-tag">UI/UX Designer & UX Researcher</div>
                  </div>

                  {/* Block 2: Title & Statement */}
                  <div className="hero-block-intro">
                    <h1 className="hero-title">Hi, I'm Cheryl.</h1>
                    <p
                      className="hero-statement-primary"
                      style={{ fontSize: '1.4rem', lineHeight: '1.5' }}
                    >
                      UX designer who brings clarity to complex problems and turns insights into
                      actionable experiences.
                    </p>
                  </div>

                  {/* Block 3: Key Accomplishments & Timeline */}
                  <div className="hero-block-timeline">
                    <div className="hero-timeline">
                      <div className="hero-timeline-item">
                        <Trophy className="hero-timeline-icon" size={24} />
                        <div className="hero-timeline-text">
                          <div className="hero-timeline-title">Service Transformation</div>
                          <div className="hero-timeline-desc">
                            0→1 digital transformation · <strong>52.2% digital adoption</strong> ·
                            IASDR 2025 Best Paper Award
                          </div>
                        </div>
                      </div>
                      <div className="hero-timeline-item">
                        <Search className="hero-timeline-icon" size={24} />
                        <div className="hero-timeline-text">
                          <div className="hero-timeline-title">Research-driven Design</div>
                          <div className="hero-timeline-desc">
                            4 research projects · Stakeholder interviews · Usability testing ·
                            Service redesign
                          </div>
                        </div>
                      </div>
                      <div className="hero-timeline-item">
                        <LayoutGrid className="hero-timeline-icon" size={24} />
                        <div className="hero-timeline-text">
                          <div className="hero-timeline-title">From Design to Delivery</div>
                          <div className="hero-timeline-desc">
                            10+ websites, systems & apps · Cross-functional collaboration · 3+ new
                            client projects
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Scroll-down button — below hashtags, positioned relative to hero section */}
            <div className="hero-scroll-btn-wrap">
              <button
                className="hero-scroll-btn"
                onClick={() => {
                  document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' })
                }}
                aria-label="View Selected Works"
              >
                View Selected Works
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M7 13l5 5 5-5" />
                  <path d="M7 6l5 5 5-5" />
                </svg>
              </button>
            </div>
          </section>
        )}

        {/* Scroll-over content wrapper — slides up to cover hero */}
        <div className="scroll-over-content">
          {/* Work Section */}
          <section
            id="work"
            className="port-section-wrapper"
            style={isProjectOpen ? { paddingTop: '0px', scrollMarginTop: 0 } : undefined}
          >
            <WorkView deselectFlag={deselectFlag} initialSlug={initialSlug} />
          </section>

          {/* Full-width Divider Line between Selected Works & Research & Recognition */}
          {!isProjectOpen && <div className="section-divider-line" />}

          {/* Research Publications Section */}
          {!isProjectOpen && (
            <section id="research" className="port-section-wrapper">
              <div className="publications-section">
                <div className="publications-container">
                  <div className="research-section-header" style={{ marginBottom: '1.5rem' }}>
                    <div
                      className="work-intro-header"
                      style={{ position: 'relative', padding: '0.5rem 0' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          position: 'relative',
                          zIndex: 1,
                        }}
                      >
                        <h2
                          className="section-title"
                          style={{
                            fontFamily: 'var(--font-sans)',
                            fontSize: '1.15rem',
                            fontWeight: 800,
                            letterSpacing: '-0.02em',
                            margin: 0,
                            textAlign: 'center',
                          }}
                        >
                          # Research & Publications
                        </h2>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Full-width marquee outside container padding */}
                <div className="research-marquee-wrapper">
                  <div className="research-marquee-track" ref={marqueeRef}>
                    {/* We repeat the set of items multiple times (e.g. 6) to ensure
                        it fills even ultra-wide monitors and loops seamlessly.
                        On mobile we only render ONE set (3 cards) so exactly 3
                        cards show — no CSS hiding needed. */}
                    {Array.from({ length: isMobile ? 1 : 6 }).map((_, setIndex) => (
                      <React.Fragment key={setIndex}>
                        <a
                          href="https://medium.com/ditl/%E5%A6%82%E4%BD%95%E5%B0%87%E5%8F%A3%E8%AA%9E%E5%88%86%E6%9E%90%E6%B3%95%E8%88%87-elements-of-ux-%E6%87%89%E7%94%A8%E6%96%BC%E7%B6%B2%E7%AB%99%E4%BD%BF%E7%94%A8%E8%80%85%E9%AB%94%E9%A9%97%E5%88%86%E6%9E%90-805628f7508d"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="recognition-list-item marquee-item external-card"
                        >
                          <div className="recognition-list-header">
                            <span className="recognition-list-award standard">
                              Medium Articles · 2024
                            </span>
                            <span className="external-badge-icon" title="另開新分頁">
                              <svg
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                <polyline points="15 3 21 3 21 9" />
                                <line x1="10" y1="14" x2="21" y2="3" />
                              </svg>
                            </span>
                          </div>
                          <div className="recognition-list-content">
                            <h3 className="recognition-list-title">
                              Chunghwa Post Website Usability Study
                            </h3>
                            <p className="recognition-list-authors">
                              Think Aloud · Task Testing · Heuristic Review
                            </p>
                          </div>
                        </a>

                        <a
                          href="https://dl.designresearchsociety.org/iasdr/iasdr2025/fullpapers/180/"
                          target="_blank"
                          rel="noreferrer"
                          className="recognition-list-item marquee-item external-card"
                        >
                          <div className="recognition-list-header">
                            <span className="recognition-list-award highlight">
                              Best Paper Award · IASDR 2025
                            </span>
                            <span className="external-badge-icon" title="另開新分頁">
                              <svg
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                <polyline points="15 3 21 3 21 9" />
                                <line x1="10" y1="14" x2="21" y2="3" />
                              </svg>
                            </span>
                          </div>
                          <div className="recognition-list-content">
                            <h3 className="recognition-list-title">
                              Utilizing AI-Assisted Service Design to Address the Digitalization
                              Challenges of Religious Services: A Case Study of Taoist Temple
                              Service
                            </h3>
                            <p className="recognition-list-authors">
                              Shin-Ying, Chen, Hsien-Hui Tang
                            </p>
                          </div>
                        </a>

                        <a
                          href="https://etheses.lib.ntust.edu.tw/detail/71f48f7560dd955aae652ea5036b99cc/"
                          target="_blank"
                          rel="noreferrer"
                          className="recognition-list-item marquee-item external-card"
                        >
                          <div className="recognition-list-header">
                            <span className="recognition-list-award standard">
                              Master's Thesis · NTUST 2026
                            </span>
                            <span className="external-badge-icon" title="另開新分頁">
                              <svg
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                <polyline points="15 3 21 3 21 9" />
                                <line x1="10" y1="14" x2="21" y2="3" />
                              </svg>
                            </span>
                          </div>
                          <div className="recognition-list-content">
                            <h3 className="recognition-list-title">
                              AI-Assisted Service Design in the Digital Transformation of Religious
                              Services
                            </h3>
                            <p className="recognition-list-authors">
                              Shin-Ying, Chen · Advisor: Prof. Hsien-Hui Tang
                            </p>
                          </div>
                        </a>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* ── Redesigned Footer ── */}
          <footer
            className={`port-footer-new${isProjectOpen ? ' is-project-page' : ''}`}
            style={{
              position: 'relative',
              paddingTop: isProjectOpen ? '0' : '3rem',
              paddingBottom: '0',
              paddingLeft: '3rem',
              paddingRight: '3rem',
              marginTop: 0,
              boxSizing: 'border-box',
            }}
          >
            {!isProjectOpen && (
              <>
                <div
                  className="port-footer-new-inner"
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      marginBottom: '0.75rem',
                      marginTop: 0,
                      gap: '1.5rem',
                    }}
                  >
                    <div className="footer-connect-divider-left" />
                    <h2 className="port-footer-heading">Let's Connect</h2>
                    <div className="footer-connect-divider-right" />
                  </div>

                  <p className="port-footer-subheading">
                    Always open to new opportunities and conversations.
                  </p>

                  <div
                    className="port-footer-cta-row"
                    style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}
                  >
                    <a href="mailto:cxytw77@gmail.com" title="Email" className="social-icon-btn">
                      <Mail size={20} />
                    </a>
                    <a
                      href="https://www.linkedin.com/in/shinyingchen/"
                      title="LinkedIn"
                      target="_blank"
                      rel="noreferrer"
                      className="social-icon-btn"
                    >
                      <Linkedin size={20} />
                    </a>
                    <a
                      href="https://medium.com/@xying77"
                      title="Medium"
                      target="_blank"
                      rel="noreferrer"
                      className="social-icon-btn"
                    >
                      <Medium size={20} />
                    </a>
                  </div>
                </div>

                <div className="port-footer-premium">
                  <div className="port-footer-premium-inner">
                    <span className="port-footer-copy">
                      © {new Date().getFullYear()} Cheryl Chen.
                    </span>
                    <span className="port-footer-loc">Taipei, Taiwan</span>
                  </div>
                </div>
              </>
            )}

            {isProjectOpen && (
              <div
                className="port-footer-project-bar"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '1rem 0',
                  fontFamily: 'var(--font-sans)',
                  fontSize: '0.8rem',
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontWeight: 500,
                  letterSpacing: '0.02em',
                  flexWrap: 'wrap',
                }}
              >
                <span>© {new Date().getFullYear()} Cheryl Chen.</span>
                <span style={{ opacity: 0.4 }}>|</span>
                <span>Taipei, Taiwan</span>
                <span style={{ opacity: 0.4 }}>|</span>
                <a
                  href="mailto:cxytw77@gmail.com"
                  title="Email"
                  className="social-icon-btn"
                  style={{ width: '36px', height: '36px', color: 'rgba(255,255,255,0.7)' }}
                >
                  <Mail size={18} />
                </a>
                <a
                  href="https://www.linkedin.com/in/shinyingchen/"
                  title="LinkedIn"
                  target="_blank"
                  rel="noreferrer"
                  className="social-icon-btn"
                  style={{ width: '36px', height: '36px', color: 'rgba(255,255,255,0.7)' }}
                >
                  <Linkedin size={18} />
                </a>
                <a
                  href="https://medium.com/@xying77"
                  title="Medium"
                  target="_blank"
                  rel="noreferrer"
                  className="social-icon-btn"
                  style={{ width: '36px', height: '36px', color: 'rgba(255,255,255,0.7)' }}
                >
                  <Medium size={18} />
                </a>
              </div>
            )}
          </footer>
        </div>
        {/* end scroll-over-content */}
      </main>
    </div>
  )
}

// trigger rebuild
